'use client';

import type { CSSProperties, ReactNode, Ref } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { clsx } from 'clsx';
import DisclosureRightIcon from '../../icons/react/icon-disclosure-right';
import type { Space } from '../../styles/tokens';
import { buildResponsiveProps, type ResponsiveValue } from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import { HorizontalStack } from '../alignment/horizontal-stack';
import styles from './accordion.module.scss';

const componentStyleName = 'accordion';

export type AccordionHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface AccordionOwnProps {
  children?: ReactNode;
  className?: string;
  gap?: ResponsiveValue<Space>;
  ref?: Ref<HTMLDivElement>;
}

type SingleRootProps = Omit<AccordionPrimitive.AccordionSingleProps, 'asChild' | 'orientation'>;
type MultipleRootProps = Omit<AccordionPrimitive.AccordionMultipleProps, 'asChild' | 'orientation'>;

export type AccordionProps = (SingleRootProps | MultipleRootProps) & AccordionOwnProps;

export function Accordion(props: AccordionProps) {
  const { className, gap = 4, style } = props;

  const rootClassName = clsx(styles.accordion, className);
  const rootStyle = sanitizeCSSVariables({
    ...style,
    ...buildResponsiveProps(componentStyleName, 'gap', gap, 'space'),
  } as CSSProperties);

  if (props.type === 'single') {
    const { collapsible = true, gap: _gap, ...rest } = props;

    return <AccordionPrimitive.Root {...rest} className={rootClassName} collapsible={collapsible} style={rootStyle} />;
  }

  const { gap: _gap, ...rest } = props;

  return <AccordionPrimitive.Root {...rest} className={rootClassName} style={rootStyle} />;
}

export interface AccordionItemProps extends Omit<AccordionPrimitive.AccordionItemProps, 'asChild'> {
  ref?: Ref<HTMLDivElement>;
}

export function AccordionItem({ className, ...rest }: AccordionItemProps) {
  return <AccordionPrimitive.Item {...rest} className={clsx(styles.item, className)} />;
}

export interface AccordionTriggerProps extends Omit<AccordionPrimitive.AccordionTriggerProps, 'asChild'> {
  headingLevel?: AccordionHeadingLevel;
  icon?: ReactNode;
  pill?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function AccordionTrigger({ children, className, headingLevel = 3, icon, pill, ref, ...rest }: AccordionTriggerProps) {
  const Heading = `h${headingLevel}` as const;

  return (
    <AccordionPrimitive.Header asChild>
      <Heading className={styles.header}>
        <AccordionPrimitive.Trigger {...rest} className={clsx(styles.trigger, className)} ref={ref}>
          {icon ? <span className={styles.icon}>{icon}</span> : null}

          <span className={styles.label}>{children}</span>

          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            {pill}

            <span className={styles.chevron}>
              <DisclosureRightIcon aria-hidden height={16} width={16} />
            </span>
          </HorizontalStack>
        </AccordionPrimitive.Trigger>
      </Heading>
    </AccordionPrimitive.Header>
  );
}

export interface AccordionContentProps extends Omit<AccordionPrimitive.AccordionContentProps, 'asChild' | 'forceMount'> {
  ref?: Ref<HTMLDivElement>;
}

export function AccordionContent({ className, ...rest }: AccordionContentProps) {
  return <AccordionPrimitive.Content {...rest} className={clsx(styles.content, className)} />;
}
